<template>
  <div id='cards' class='box'>
    <vue-loading :active.sync='loading' :is-full-page='true' loader='dots' color='#70A1F0'></vue-loading>
    <div class='row'>
      <div class='column'>
        <div class='row'>
          <div class='column'>
            <div>
              <transition name='fade' appear>
                <CostsMetricsGraph
                  v-if='metrics && metrics.length > 0 && activeAccount === null'
                  :metrics='metrics'
                  :name='CostsMetrics'
                  :id='id'
                  :activeType='activeType'
                  :key='id'
                />
              </transition>
              <transition name='fade' appear>
                <CostsAccountGraph
                  v-if='costs && Object.keys(costs).length > 0 && activeAccount != null'
                  @update-date='handleDateUpdate'
                  :costs='costs'
                  :name='CostsAccount'
                  :activeType='activeType'
                  :activeAccount='activeAccount'
                  :key='`${id}${activeAccount}`'
                />
              </transition>
            </div>
          </div>
        </div>
      </div>
      <div class='col'>
        <div class='row'>
          <div class='col'>
            <transition name='fade' appear>
              <div v-if="displayBoards.length > 0 && activeType === 'group'">
                <div class='card p-3 shadow-sm'>
                  <dl class='m-0'>
                    <template v-for='contract in displayBoards'>
                      <dt :key='contract.contract'
                        @mouseover='hoverContract = contract.contract'
                        @mouseout='hoverContract = null'
                        @click='updateContract(contract.contract)'
                        :class='[
                          { "hover-contract": hoverContract === contract.contract },
                          { "inactive-contract": hoverContract !== contract.contract }
                        ]'
                        class='contracts py-2 px-3 border-bottom'
                      >
                        {{ contract.contract }}
                      </dt>
                      <dd :key="contract.contract + '-details'" class="contract-details px-3 py-2">
                        <div class='d-flex justify-content-between align-items-center'>
                          <strong>Monthly Cost:</strong>
                          <span>{{ '$' + contract.cost.toFixed(2) }}</span>
                        </div>
                        <div class='d-flex justify-content-between align-items-center mt-1'>
                          <span class='text-muted'>Historical Change:</span>
                          <span :class='changeClass(contract.historical_percentage_change)'>
                            {{ formatPercentage(contract.historical_percentage_change) }}
                          </span>
                        </div>
                        <div class='d-flex justify-content-between align-items-center mt-1'>
                          <span class='text-muted'>Predicted Change:</span>
                          <span :class='changeClass(contract.predicted_change)'>
                            {{ formatPercentage(contract.predicted_change) }}
                          </span>
                        </div>
                      </dd>
                    </template>
                  </dl>
                </div>
              </div>
            </transition>
            <transition name='fade' appear>
              <div v-if="displayAccounts.length > 0 && activeType === 'contract'">
                <div class='card p-3 shadow-sm'>
                  <dl class='m-0'>
                    <template v-for='account in displayAccounts'>
                      <dt :key='account.account'
                        @click='setActiveAccount(account.account)'
                        @mouseover='hoverAccount = account.account'
                        @mouseout='hoverAccount = null'
                        :class='accountClass(account)'
                        class='members-list py-2 px-3 border-bottom'
                      >
                        {{ account.account }}
                      </dt>
                      <dd :key="account.account + '-details'" class="contract-details px-3 py-2">
                        <div class='d-flex justify-content-between align-items-center'>
                          <strong>Monthly Cost:</strong>
                          <span>{{ '$' + account.cost.toFixed(2) }}</span>
                        </div>
                        <div class='d-flex justify-content-between align-items-center mt-1'>
                          <span class='text-muted'>Historical Change:</span>
                          <span :class='changeClass(account.historical_percentage_change)'>
                            {{ formatPercentage(account.historical_percentage_change) }}
                          </span>
                        </div>
                        <div class='d-flex justify-content-between align-items-center mt-1'>
                          <span class='text-muted'>Predicted Change:</span>
                          <span :class='changeClass(account.predicted_change)'>
                            {{ formatPercentage(account.predicted_change) }}
                          </span>
                        </div>
                      </dd>
                  </template>
                  </dl>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>

    </div>
    <div class='row'>
      <div class='column'>
        <transition name='fade' appear>
          <vue-good-table
            v-if='costs && Object.keys(costs).length > 0 && activeAccount != null'
            :columns='columns'
            :rows='formattedCosts'
            :search-options='{ enabled: true, skipDiacritics: true, placeholder: "Filter" }'
            :pagination-options='{ enabled: true, perPage: 20 }'
            styleClass='vgt-table condensed'
            :key='`${id}${activeAccount}${date}`'
          >
          </vue-good-table>
        </transition>
      </div>
      <div class='column'>
      </div>
    </div>
  </div>
</template>

<script>
import HTTP from '@/common/http'
import CostsMetricsGraph from '@/components/CostsMetricsGraph'
import CostsAccountGraph from '@/components/CostsAccountGraph'
import { mapGetters } from 'vuex'
import VueLoading from 'vue-loading-overlay'

export default {
  components: {
    CostsMetricsGraph,
    CostsAccountGraph,
    VueLoading
  },
  props: ['id'],
  data () {
    return {
      metrics: [],
      costs: [],
      report: { contracts: [], accounts: [], costs: [] },
      interval: '',
      activeAccount: null,
      hoverAccount: null,
      hoverContract: null,
      date: null,
      loading: false,
      columns: [
        { label: 'Service', field: 'Service' },
        { label: 'Cost', field: 'Cost' }
      ]
    }
  },
  computed: {
    ...mapGetters(['settings', 'activeType']),
    displayBoards () {
      return [...this.report.contracts].sort((a, b) =>
        Math.abs(b.historical_percentage_change) - Math.abs(a.historical_percentage_change)
      )
    },
    displayAccounts () {
      return this.report.accounts
        .filter(account => account.contract === this.id)
        .sort((a, b) => Math.abs(b.historical_percentage_change) - Math.abs(a.historical_percentage_change))
    },
    formattedCosts () {
      return Object.entries(this.costs)
        .filter(([account]) => account === this.activeAccount)
        .flatMap(([account, months]) => months[this.date] || [])
        .map(service => ({
          Service: service.service,
          Cost: service.amount,
          Date: service.date,
          AccountId: service.AccountId,
          AccountName: service.AccountName,
          Contract: service.Contract,
          Region: service.region
        }))
    }
  },
  created () {
    this.getMetrics(true)
  },
  destroyed () {
    clearInterval(this.interval)
  },
  watch: {
    id () {
      this.resetData()
      this.getMetrics(true)
    },
    report: { deep: true }
  },
  methods: {
    resetData () {
      this.report = { contracts: [], accounts: [], costs: [] }
      this.metrics = []
      this.hoverContract = null
      this.activeAccount = null
    },
    getMetrics (showLoading) {
      this.loading = showLoading
      return HTTP.get(`/costs/metrics/${this.activeType}/${this.id}/month`)
        .then(response => {
          const monthMap = {
            '01': 'Jan',
            '02': 'Feb',
            '03': 'Mar',
            '04': 'Apr',
            '05': 'May',
            '06': 'Jun',
            '07': 'Jul',
            '08': 'Aug',
            '09': 'Sep',
            '10': 'Oct',
            '11': 'Nov',
            '12': 'Dec'
          }
          this.metrics = response.data.metrics
          this.date = monthMap[this.metrics[11].date.split('-')[1]]
          this.report.contracts = this.metrics[11].contracts
          this.report.accounts = this.metrics[11].accounts
        })
        .catch(error => {
          this.metrics = []
          this.flashMessage.error({
            message: `Failed to get costs metrics: ${error.response ? error.response.data.error : error.message}`
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    getSummary (showLoading) {
      this.loading = showLoading
      HTTP.get(`costs/summary/${this.activeType}/${this.id}`)
        .then(response => {
          this.costs = response.data.costs
        })
        .catch(error => {
          this.flashMessage.error({
            message: `Failed to get costs: ${error.response ? error.response.data.error : error.message}`
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    setActiveAccount (account) {
      this.activeAccount = this.activeAccount === account ? null : account
      this.getSummary(true)
    },
    updateContract (contract) {
      this.activeAccount = null
      this.$store.dispatch('SET_ACTIVE', contract)
    },
    handleDateUpdate (newDate) {
      this.date = newDate
    },
    changeClass (percentage) {
      return percentage > 0 ? 'positive-diff' : 'negative-diff'
    },
    formatPercentage (percentage) {
      return percentage > 0 ? `+${percentage.toFixed(2)}%` : `${percentage.toFixed(2)}%`
    },
    accountClass (account) {
      return [
        { 'active-member': this.activeAccount === account.account },
        { 'hover-member': this.hoverAccount === account.account && this.activeAccount !== account.account },
        { 'inactive-member': this.activeAccount !== account.account && this.hoverAccount !== account.account }
      ]
    }
  }
}
</script>

<style scoped src='../assets/stylesheet/cards.css'></style>



// WEBPACK FOOTER //
// src/views/Costs.vue